import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { api, useAuth } from '../services/AuthService';
import { escapeHtml } from '../utils/escapeHtml';

function FechamentoCaixa() {
  const theme = useTheme();
  const { isAdminMaster, getLanchoneteId } = useAuth();
  
  const getLocalDateString = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(getLocalDateString(new Date()));
  const [pedidos, setPedidos] = useState([]);
  const [lanchonete, setLanchonete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [printingCategory, setPrintingCategory] = useState(null);
  const printLockRef = useRef(false);
  const pedidosRequestRef = useRef(0);

  const loadLanchonete = useCallback(async () => {
    try {
      const lanchoneteId = getLanchoneteId();
      if (lanchoneteId) {
        const response = await api.get(`/lanchonetes/${lanchoneteId}`);
        setLanchonete(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados da lanchonete:', error);
    }
  }, [getLanchoneteId]);

  const loadPedidos = useCallback(async (targetDate) => {
    const requestId = ++pedidosRequestRef.current;
    try {
      setLoading(true);
      setLoadError(false);
      let url = `/pedidos?date=${targetDate}`;
      
      if (!isAdminMaster()) {
        const lanchoneteId = getLanchoneteId();
        if (!lanchoneteId) {
          if (requestId === pedidosRequestRef.current) setPedidos([]);
          return;
        }
        url += `&lanchonete_id=${lanchoneteId}`;
      }
      
      // A listagem da API tem limite de 100 por página. Buscar todas as páginas
      // evita que dias movimentados tenham fechamento parcial sem aviso.
      const pageSize = 200;
      const allPedidos = [];
      let offset = 0;
      while (true) {
        if (requestId !== pedidosRequestRef.current) return;
        const separator = url.includes('?') ? '&' : '?';
        const response = await api.get(`${url}${separator}limit=${pageSize}&offset=${offset}`);
        if (requestId !== pedidosRequestRef.current) return;
        const page = Array.isArray(response.data) ? response.data : [];
        allPedidos.push(...page);
        if (page.length < pageSize) break;
        offset += pageSize;
      }
      if (requestId === pedidosRequestRef.current) setPedidos(allPedidos);
    } catch (error) {
      if (requestId === pedidosRequestRef.current) {
        setPedidos([]);
        setLoadError(true);
        toast.error('Erro ao carregar pedidos');
        console.error('Erro ao carregar pedidos:', error);
      }
    } finally {
      if (requestId === pedidosRequestRef.current) setLoading(false);
    }
  }, [isAdminMaster, getLanchoneteId]);

  useEffect(() => {
    loadPedidos(selectedDate);
    loadLanchonete();
    return () => {
      pedidosRequestRef.current += 1;
    };
  }, [selectedDate, loadPedidos, loadLanchonete]);

  const handleQuickDate = (daysAgo) => {
    const target = new Date();
    target.setDate(target.getDate() - daysAgo);
    setSelectedDate(getLocalDateString(target));
  };

  const formatBrl = (val) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(val || 0);
  };

  const formatDateBrl = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  // Pedidos cancelados e aguardando pagamento não representam venda recebida.
  // Devoluções parciais são abatidas do valor financeiro do fechamento.
  const pedidosFinanceiros = pedidos.filter((pedido) => {
    const status = String(pedido.status || '').toLowerCase();
    const paymentStatus = String(pedido.payment_status || '').toLowerCase();
    const paymentMethod = String(pedido.payment_method || '').toLowerCase();
    const isOnlinePayment = ['pix', 'card', 'credit_card', 'debit_card', 'mercadopago', 'mercado_pago'].includes(paymentMethod);
    const onlinePaymentNotReceived = isOnlinePayment && ['pending', 'unpaid', 'failed', 'rejected'].includes(paymentStatus);
    return !['cancelado', 'cancelled', 'canceled', 'payment_pending'].includes(status)
      && !['refunded'].includes(paymentStatus)
      && !onlinePaymentNotReceived;
  });

  const getPedidoFinancialValue = (pedido) => Math.max(0,
    Number(pedido.total_amount || pedido.total || 0) - Number(pedido.refunded_amount || 0)
  );

  // Cálculo das métricas para exibição na tela
  const getMetrics = () => {
    let totalRevenue = 0;
    let countApp = 0;
    let revenueApp = 0;
    let countMesas = 0;
    let revenueMesas = 0;

    const paymentMethods = {
      pix: { count: 0, sum: 0 },
      credit: { count: 0, sum: 0 },
      debit: { count: 0, sum: 0 },
      card: { count: 0, sum: 0 },
      money: { count: 0, sum: 0 },
      other: { count: 0, sum: 0 }
    };

    const deliveryTypes = {
      retirada: { count: 0, sum: 0 },
      app: { count: 0, sum: 0 },
      loja: { count: 0, sum: 0 },
      mesa: { count: 0, sum: 0 }
    };

    pedidosFinanceiros.forEach((p) => {
      const val = getPedidoFinancialValue(p);
      totalRevenue += val;

      const isMesa = !!p.mesa_id;
      if (isMesa) {
        countMesas++;
        revenueMesas += val;
      } else {
        countApp++;
        revenueApp += val;
      }

      // Pagamentos
      const method = String(p.payment_method || '').toLowerCase();
      if (method.includes('pix')) {
        paymentMethods.pix.count++;
        paymentMethods.pix.sum += val;
      } else if (method.includes('debito') || method.includes('debit')) {
        paymentMethods.debit.count++;
        paymentMethods.debit.sum += val;
      } else if (method.includes('credito') || method.includes('credit')) {
        paymentMethods.credit.count++;
        paymentMethods.credit.sum += val;
      } else if (method === 'card' || method.includes('cartao') || method.includes('card')) {
        paymentMethods.card.count++;
        paymentMethods.card.sum += val;
      } else if (method.includes('dinheiro') || method.includes('money') || method.includes('cash') || method.includes('delivery')) {
        paymentMethods.money.count++;
        paymentMethods.money.sum += val;
      } else {
        paymentMethods.other.count++;
        paymentMethods.other.sum += val;
      }

      // Entregas
      const tipo = String(p.tipo_entrega || '').toUpperCase();
      if (tipo === 'RETIRADA') {
        deliveryTypes.retirada.count++;
        deliveryTypes.retirada.sum += val;
      } else if (tipo === 'APP') {
        deliveryTypes.app.count++;
        deliveryTypes.app.sum += val;
      } else if (tipo === 'LOJA') {
        deliveryTypes.loja.count++;
        deliveryTypes.loja.sum += val;
      } else if (tipo === 'MESA' || isMesa) {
        deliveryTypes.mesa.count++;
        deliveryTypes.mesa.sum += val;
      }
    });

    const averageTicket = pedidosFinanceiros.length > 0 ? totalRevenue / pedidosFinanceiros.length : 0;

    return {
      totalRevenue,
      countApp,
      revenueApp,
      countMesas,
      revenueMesas,
      paymentMethods,
      deliveryTypes,
      averageTicket
    };
  };

  const metrics = getMetrics();

  // Função de Impressão do Fechamento
  const handlePrintCategory = async (category) => {
    if (printLockRef.current || printingCategory) return;

    // O ref bloqueia imediatamente. O estado React sozinho so atualiza no
    // proximo render e permitia dois ou mais cliques/disparos muito rapidos.
    printLockRef.current = true;
    setPrintingCategory(category);

    try {
      const filtered = pedidosFinanceiros.filter((p) => {
        if (category === 'app') return !p.mesa_id;
        if (category === 'mesas') return !!p.mesa_id;
        return true;
      });

      const periodLabel = formatDateBrl(selectedDate);
      const safeStoreName = escapeHtml(lanchonete?.name || 'Não informada');

      if (filtered.length === 0) {
        toast.info(`Nenhum pedido de ${category === 'app' ? 'aplicativo' : 'mesas'} para imprimir (${periodLabel}).`);
        printLockRef.current = false;
        setPrintingCategory(null);
        return;
      }

      const brl = (v) => `R$ ${Number(v || 0).toFixed(2)}`;

      let countPix = 0, sumPix = 0;
      let countCredit = 0, sumCredit = 0;
      let countDebit = 0, sumDebit = 0;
      let countCard = 0, sumCard = 0;
      let countMoney = 0, sumMoney = 0;
      let countOther = 0, sumOther = 0;

      let countRetirada = 0, sumRetirada = 0;
      let countEntregaApp = 0, sumEntregaApp = 0;
      let countEntregaLoja = 0, sumEntregaLoja = 0;
      let countMesa = 0, sumMesa = 0;

      let totalGeral = 0;

      filtered.forEach(p => {
        const val = getPedidoFinancialValue(p);
        totalGeral += val;

        const method = String(p.payment_method || '').toLowerCase();
        if (method.includes('pix')) {
          countPix++;
          sumPix += val;
        } else if (method.includes('debito') || method.includes('debit')) {
          countDebit++;
          sumDebit += val;
        } else if (method.includes('credito') || method.includes('credit')) {
          countCredit++;
          sumCredit += val;
        } else if (method === 'card' || method.includes('cartao') || method.includes('card')) {
          countCard++;
          sumCard += val;
        } else if (method.includes('dinheiro') || method.includes('money') || method.includes('cash') || method.includes('delivery')) {
          countMoney++;
          sumMoney += val;
        } else {
          countOther++;
          sumOther += val;
        }

        const tipo = String(p.tipo_entrega || '').toUpperCase();
        if (tipo === 'RETIRADA') {
          countRetirada++;
          sumRetirada += val;
        } else if (tipo === 'APP') {
          countEntregaApp++;
          sumEntregaApp += val;
        } else if (tipo === 'LOJA') {
          countEntregaLoja++;
          sumEntregaLoja += val;
        } else if (tipo === 'MESA' || p.mesa_id) {
          countMesa++;
          sumMesa += val;
        }
      });

      const w = window.open('', '_blank');
      if (!w) {
        printLockRef.current = false;
        setPrintingCategory(null);
        toast.error('Permita pop-ups para imprimir o relatório');
        return;
      }

      const html = `
        <html>
          <head>
            <title>Fechamento de Caixa - ${category === 'app' ? 'Aplicativo' : 'Mesas'}</title>
            <style>
              * {
                color: #000000 !important;
                font-weight: 900 !important;
                box-sizing: border-box;
              }
              body { 
                font-family: 'Segoe UI', Arial, sans-serif; 
                padding: 5px; 
                width: 240px;
                margin: 0 auto;
              }
              .header { text-align: center; margin-bottom: 15px; border-bottom: 2.5px dashed #000000; padding-bottom: 10px; }
              .header h1 { margin: 0; font-size: 16px; text-transform: uppercase; }
              .header p { margin: 5px 0 0 0; font-size: 11px; }
              
              .summary-container { display: block; margin-bottom: 15px; }
              .summary-card { border: 2px solid #000000; padding: 10px; border-radius: 6px; background-color: #ffffff; text-align: center; margin-bottom: 10px; }
              .summary-card h3 { margin: 0 0 5px 0; font-size: 11px; text-transform: uppercase; }
              .summary-card p { margin: 0; font-size: 18px; }

              .section-title { font-size: 12px; margin: 15px 0 5px 0; border-bottom: 2px solid #000000; padding-bottom: 3px; text-transform: uppercase; }
              .grid-container { display: block; }
              .grid-half { border: 2px solid #000000; border-radius: 6px; padding: 8px; background-color: #ffffff; margin-bottom: 12px; }
              
              .list-item { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1.5px dashed #000000; font-size: 11px; }
              .list-item:last-child { border-bottom: none; }
              
              @media print {
                body { padding: 0; width: 100%; max-width: 240px; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Fechamento - ${category === 'app' ? 'Aplicativo' : 'Mesas'}</h1>
              <p>Lanchonete: ${safeStoreName}</p>
              <p>Período: ${escapeHtml(periodLabel)}</p>
              <p>Emissão: ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
            </div>
            
            <div class="summary-container">
              <div class="summary-card">
                <h3>Total de Pedidos</h3>
                <p>${filtered.length}</p>
              </div>
              <div class="summary-card">
                <h3>Faturamento do Dia</h3>
                <p>${brl(totalGeral)}</p>
              </div>
            </div>

            <div class="grid-container">
              <div class="grid-half">
                <div class="section-title" style="margin-top: 0; border-bottom: 1.5px solid #000;">Meios de Pagamento</div>
                <div class="list-item">
                  <span>Pix</span>
                  <span>${countPix}x (${brl(sumPix)})</span>
                </div>
                <div class="list-item">
                  <span>Cartão (crédito identificado)</span>
                  <span>${countCredit}x (${brl(sumCredit)})</span>
                </div>
                <div class="list-item">
                  <span>Cartão de Débito</span>
                  <span>${countDebit}x (${brl(sumDebit)})</span>
                </div>
                <div class="list-item">
                  <span>Cartão (tipo não informado)</span>
                  <span>${countCard}x (${brl(sumCard)})</span>
                </div>
                <div class="list-item">
                  <span>Dinheiro</span>
                  <span>${countMoney}x (${brl(sumMoney)})</span>
                </div>
                <div class="list-item">
                  <span>Outros</span>
                  <span>${countOther}x (${brl(sumOther)})</span>
                </div>
              </div>

              <div class="grid-half">
                <div class="section-title" style="margin-top: 0; border-bottom: 1.5px solid #000;">Modos de Entrega / Serviço</div>
                ${category === 'app' ? `
                  <div class="list-item">
                    <span>Retirada no Local</span>
                    <span>${countRetirada}x (${brl(sumRetirada)})</span>
                  </div>
                  <div class="list-item">
                    <span>Entrega (App)</span>
                    <span>${countEntregaApp}x (${brl(sumEntregaApp)})</span>
                  </div>
                  <div class="list-item">
                    <span>Entrega (Loja)</span>
                    <span>${countEntregaLoja}x (${brl(sumEntregaLoja)})</span>
                  </div>
                ` : `
                  <div class="list-item">
                    <span>Consumo Local (Mesa)</span>
                    <span>${countMesa}x (${brl(sumMesa)})</span>
                  </div>
                `}
              </div>
            </div>

          </body>
        </html>
      `;
      w.document.write(html);
      w.document.close();
      let printStarted = false;
      const releasePrint = () => {
        printLockRef.current = false;
        setPrintingCategory(null);
      };
      w.onafterprint = () => {
        if (!w.closed) w.close();
        releasePrint();
      };
      setTimeout(() => {
        if (w.closed) {
          releasePrint();
          return;
        }
        if (printStarted) return;
        printStarted = true;
        w.focus();
        w.print();
      }, 300);
      // Alguns navegadores e a impressao silenciosa nao emitem afterprint.
      // Fecha a janela auxiliar e libera o botao, sem chamar print novamente.
      setTimeout(() => {
        if (!w.closed) w.close();
        releasePrint();
      }, 15000);
    } catch (error) {
      printLockRef.current = false;
      setPrintingCategory(null);
      toast.error('Erro ao gerar relatório de impressão');
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    switch (String(status).toLowerCase()) {
      case 'pendente':
      case 'pending':
        return 'warning';
      case 'aceito':
        return 'info';
      case 'preparando':
      case 'preparing':
        return 'secondary';
      case 'saiu_entrega':
      case 'delivering':
        return 'primary';
      case 'entregue':
      case 'delivered':
        return 'success';
      case 'cancelado':
      case 'cancelled':
        return 'error';
      default: return 'default';
    }
  };

  const getPaymentMethodText = (paymentMethod) => {
    switch (String(paymentMethod).toLowerCase()) {
      case 'mercadopago': return 'Mercado Pago (Online)';
      case 'pix': return '💳 Pix';
      case 'money': return '💵 Dinheiro';
      case 'pickup': return '🏪 Retirada';
      case 'local': return '🍽️ Comer no local';
      default: return `💳 ${paymentMethod || 'Não informado'}`;
    }
  };

  const getStatusText = (status) => {
    switch (String(status).toLowerCase()) {
      case 'pendente':
      case 'pending': return 'Pendente';
      case 'aceito': return 'Aceito';
      case 'preparando':
      case 'preparing': return 'Preparando';
      case 'pronto':
      case 'pedido_pronto':
      case 'ready': return 'Pedido Pronto';
      case 'saiu_entrega':
      case 'delivering': return 'Saiu para Entrega';
      case 'entregue':
      case 'delivered': return 'Entregue';
      case 'cancelado':
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight="bold">Fechamento de Caixa</Typography>
        
        {/* Controles de Data e Ações de Impressão */}
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <TextField
            label="Escolher Dia"
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ bgcolor: 'white', borderRadius: 1 }}
          />

          <Box display="flex" gap={0.5}>
            <Button variant="outlined" size="small" onClick={() => handleQuickDate(0)}>
              HOJE
            </Button>
            <Button variant="outlined" size="small" onClick={() => handleQuickDate(1)}>
              ONTEM
            </Button>
          </Box>

          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={() => handlePrintCategory('app')}
            disabled={loading || Boolean(printingCategory)}
          >
            IMPRIMIR APP
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<PrintIcon />}
            onClick={() => handlePrintCategory('mesas')}
            disabled={loading || Boolean(printingCategory)}
          >
            IMPRIMIR MESAS
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={() => loadPedidos(selectedDate)}>Tentar novamente</Button>}
        >
          Nao foi possivel carregar os pedidos de {formatDateBrl(selectedDate)}. Os valores do fechamento foram limpos para evitar mostrar ou imprimir dados antigos.
        </Alert>
      ) : (
        <>
          {/* Dashboard de Métricas Financeiras */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)', borderRadius: 2, borderLeft: `6px solid ${theme.palette.primary.main}` }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <PaidIcon color="primary" />
                    <Typography color="text.secondary" variant="subtitle2" fontWeight="bold">FATURAMENTO TOTAL</Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold">{formatBrl(metrics.totalRevenue)}</Typography>
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="caption" color="text.secondary">App: {formatBrl(metrics.revenueApp)}</Typography>
                    <Typography variant="caption" color="text.secondary">Mesas: {formatBrl(metrics.revenueMesas)}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)', borderRadius: 2, borderLeft: `6px solid ${theme.palette.secondary.main}` }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <ReceiptIcon color="secondary" />
                    <Typography color="text.secondary" variant="subtitle2" fontWeight="bold">TOTAL DE PEDIDOS</Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold">{pedidosFinanceiros.length}</Typography>
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="caption" color="text.secondary">App: {metrics.countApp}</Typography>
                    <Typography variant="caption" color="text.secondary">Mesas: {metrics.countMesas}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)', borderRadius: 2, borderLeft: '6px solid #2196F3' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <TrendingUpIcon sx={{ color: '#2196F3' }} />
                    <Typography color="text.secondary" variant="subtitle2" fontWeight="bold">TICKET MÉDIO</Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold">{formatBrl(metrics.averageTicket)}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Média de valor gasto por pedido
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Grids de Detalhes Adicionais */}
          <Grid container spacing={3} mb={4}>
            {/* Meios de Pagamento */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>Meios de Pagamento</Typography>
                <List dense>
                  <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText primary="Pix" />
                    <Typography fontWeight="bold">{metrics.paymentMethods.pix.count}x ({formatBrl(metrics.paymentMethods.pix.sum)})</Typography>
                  </ListItem>
                  <Divider />
                  <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText primary="Cartão de Crédito (identificado)" />
                    <Typography fontWeight="bold">{metrics.paymentMethods.credit.count}x ({formatBrl(metrics.paymentMethods.credit.sum)})</Typography>
                  </ListItem>
                  <Divider />
                  <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText primary="Cartão (tipo não informado)" />
                    <Typography fontWeight="bold">{metrics.paymentMethods.card.count}x ({formatBrl(metrics.paymentMethods.card.sum)})</Typography>
                  </ListItem>
                  <Divider />
                  <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText primary="Cartão de Débito" />
                    <Typography fontWeight="bold">{metrics.paymentMethods.debit.count}x ({formatBrl(metrics.paymentMethods.debit.sum)})</Typography>
                  </ListItem>
                  <Divider />
                  <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText primary="Dinheiro" />
                    <Typography fontWeight="bold">{metrics.paymentMethods.money.count}x ({formatBrl(metrics.paymentMethods.money.sum)})</Typography>
                  </ListItem>
                  <Divider />
                  <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText primary="Outros / Não informados" />
                    <Typography fontWeight="bold">{metrics.paymentMethods.other.count}x ({formatBrl(metrics.paymentMethods.other.sum)})</Typography>
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Canais de Venda e Modos de Entrega */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>Modos de Entrega / Serviço</Typography>
                <List dense>
                  <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText primary="Retirada no Local" />
                    <Typography fontWeight="bold">{metrics.deliveryTypes.retirada.count}x ({formatBrl(metrics.deliveryTypes.retirada.sum)})</Typography>
                  </ListItem>
                  <Divider />
                  <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText primary="Entrega (App)" />
                    <Typography fontWeight="bold">{metrics.deliveryTypes.app.count}x ({formatBrl(metrics.deliveryTypes.app.sum)})</Typography>
                  </ListItem>
                  <Divider />
                  <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText primary="Entrega (Loja)" />
                    <Typography fontWeight="bold">{metrics.deliveryTypes.loja.count}x ({formatBrl(metrics.deliveryTypes.loja.sum)})</Typography>
                  </ListItem>
                  <Divider />
                  <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText primary="Consumo Local (Mesa)" />
                    <Typography fontWeight="bold">{metrics.deliveryTypes.mesa.count}x ({formatBrl(metrics.deliveryTypes.mesa.sum)})</Typography>
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>

          {/* Lista de Pedidos Auditável */}
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>Auditoria de Pedidos ({formatDateBrl(selectedDate)})</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              O fechamento considera pedidos não cancelados, pagamentos online confirmados e desconta devoluções. Pedidos cancelados continuam abaixo para conferência.
            </Typography>
            {pedidos.length === 0 ? (
              <Box py={4} textAlign="center">
                <Typography color="text.secondary">Nenhum pedido encontrado para a data selecionada.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                    <TableRow>
                      <TableCell>ID / Ref</TableCell>
                      <TableCell>Cliente / Mesa</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Pagamento</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pedidos.map((pedido) => (
                      <TableRow key={pedido.id} hover>
                        <TableCell fontWeight="bold">#{pedido.lanchonete_order_number || pedido.id}</TableCell>
                        <TableCell>
                          {pedido.mesa_id ? (
                            <Chip label={`Mesa ${pedido.mesa_id}`} size="small" color="secondary" variant="outlined" />
                          ) : (
                            pedido.customer_name || 'Cliente'
                          )}
                        </TableCell>
                        <TableCell fontWeight="bold">
                          {formatBrl(pedido.total_amount || pedido.total)}
                        </TableCell>
                        <TableCell>{getPaymentMethodText(pedido.payment_method)}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusText(pedido.status)}
                            color={getStatusColor(pedido.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              setSelectedPedido(pedido);
                              setDetailDialogOpen(true);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* Modal de Detalhes do Pedido para Auditoria */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Detalhes do Pedido #{selectedPedido?.lanchonete_order_number || selectedPedido?.id}</Typography>
          <IconButton onClick={() => setDetailDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPedido && (
            <Box>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">CLIENTE</Typography>
                  <Typography variant="body1" fontWeight="medium">{selectedPedido.customer_name || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">TELEFONE</Typography>
                  <Typography variant="body1">{selectedPedido.customer_phone || 'Não informado'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">ORIGEM / TIPO</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedPedido.mesa_id ? `Mesa ${selectedPedido.mesa_id}` : `Aplicativo (${selectedPedido.tipo_entrega || 'Entrega'})`}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">HORÁRIO</Typography>
                  <Typography variant="body1">
                    {new Date(selectedPedido.created_at).toLocaleDateString('pt-BR')} {new Date(selectedPedido.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight="bold" mb={1}>ITENS</Typography>
              <List disablePadding>
                {selectedPedido.items && selectedPedido.items.map((item, idx) => (
                  <ListItem key={idx} disableGutters sx={{ py: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText
                      primary={`${item.quantity}x ${item.name || item.product_name}`}
                      secondary={item.observacao ? `Obs: ${item.observacao}` : null}
                    />
                    <Typography fontWeight="bold">{formatBrl(item.price * item.quantity)}</Typography>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">Taxa de entrega:</Typography>
                  <Typography>{formatBrl(selectedPedido.delivery_fee)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6" fontWeight="bold">Total do Pedido:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">{formatBrl(selectedPedido.total_amount || selectedPedido.total)}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)} variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FechamentoCaixa;
